import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Building,
  Upload,
  FileSpreadsheet,
  Link,
  Search,
} from 'lucide-react';
import * as XLSX from 'xlsx';

interface Supplier {
  id: number;
  code: string;
  name: string;
  description: string | null;
  cuenta_x_pagar: string | null;
  codigo_cta: string | null;
  cod_retencion: string | null;
  rtefte: number | null;
  cod_rete: string | null;
  top_rete: number | null;
  po_rete: number | null;
  cod_cc: string | null;
  is_active: number;
  created_at: string;
  updated_at: string;
}

interface SupplierForm {
  code: string;
  name: string;
  cuenta_x_pagar: string;
  codigo_cta: string;
  cod_retencion: string;
  rtefte: string;
  cod_rete: string;
  top_rete: string;
  po_rete: string;
  cod_cc: string;
}

const emptyForm: SupplierForm = {
  code: '',
  name: '',
  cuenta_x_pagar: '',
  codigo_cta: '',
  cod_retencion: '',
  rtefte: '',
  cod_rete: '',
  top_rete: '',
  po_rete: '',
  cod_cc: '',
};

interface ImportResult {
  imported: number;
  total_submitted: number;
  valid_items: number;
  errors: string[];
}

export default function AdminSuppliers() {
  const navigate = useNavigate();
  const { usuario: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Search & pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 20;

  // Modals
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState<{ open: boolean; supplier: Supplier | null }>({ open: false, supplier: null });
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; supplier: Supplier | null }>({ open: false, supplier: null });
  const [importResultModal, setImportResultModal] = useState<{ open: boolean; result: ImportResult | null }>({ open: false, result: null });

  // Form state
  const [createForm, setCreateForm] = useState<SupplierForm>({ ...emptyForm });
  const [editForm, setEditForm] = useState<SupplierForm>({ ...emptyForm });

  // Processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState('');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Query suppliers
  const { data, isLoading } = useQuery({
    queryKey: ['admin-suppliers', debouncedSearch, page],
    queryFn: () => {
      const params: Record<string, string> = {
        page: String(page),
        per_page: String(perPage),
      };
      if (debouncedSearch) {
        params.search = debouncedSearch;
      }
      return api.getSuppliers(params);
    },
  });

  const suppliers: Supplier[] = data?.data || [];
  const totalSuppliers = data?.total || 0;
  const totalPages = data?.total_pages || 1;

  // Helpers to build request payload from form
  const formToPayload = (form: SupplierForm) => ({
    code: form.code,
    name: form.name,
    cuenta_x_pagar: form.cuenta_x_pagar || null,
    codigo_cta: form.codigo_cta || null,
    cod_retencion: form.cod_retencion || null,
    rtefte: form.rtefte ? Number(form.rtefte) : null,
    cod_rete: form.cod_rete || null,
    top_rete: form.top_rete ? Number(form.top_rete) : null,
    po_rete: form.po_rete ? Number(form.po_rete) : null,
    cod_cc: form.cod_cc || null,
  });

  const supplierToForm = (s: Supplier): SupplierForm => ({
    code: s.code || '',
    name: s.name || '',
    cuenta_x_pagar: s.cuenta_x_pagar || '',
    codigo_cta: s.codigo_cta || '',
    cod_retencion: s.cod_retencion || '',
    rtefte: s.rtefte != null ? String(s.rtefte) : '',
    cod_rete: s.cod_rete || '',
    top_rete: s.top_rete != null ? String(s.top_rete) : '',
    po_rete: s.po_rete != null ? String(s.po_rete) : '',
    cod_cc: s.cod_cc || '',
  });

  // CRUD Handlers
  const handleCreate = async () => {
    setError('');
    if (!createForm.code || !createForm.name) {
      setError('NIT y Nombre son requeridos');
      return;
    }

    setIsProcessing(true);
    try {
      await api.createSupplier(formToPayload(createForm));
      queryClient.invalidateQueries({ queryKey: ['admin-suppliers'] });
      setCreateModal(false);
      setCreateForm({ ...emptyForm });
    } catch (err: any) {
      setError(err.message || 'Error al crear proveedor');
    } finally {
      setIsProcessing(false);
    }
  };

  const openEditModal = (supplier: Supplier) => {
    setEditForm(supplierToForm(supplier));
    setEditModal({ open: true, supplier });
    setError('');
  };

  const handleEdit = async () => {
    if (!editModal.supplier) return;
    setError('');

    setIsProcessing(true);
    try {
      await api.updateSupplier(editModal.supplier.id, formToPayload(editForm));
      queryClient.invalidateQueries({ queryKey: ['admin-suppliers'] });
      setEditModal({ open: false, supplier: null });
    } catch (err: any) {
      setError(err.message || 'Error al actualizar proveedor');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.supplier) return;
    setIsProcessing(true);
    try {
      await api.deleteSupplier(deleteModal.supplier.id);
      queryClient.invalidateQueries({ queryKey: ['admin-suppliers'] });
      setDeleteModal({ open: false, supplier: null });
    } catch (err: any) {
      setError(err.message || 'Error al eliminar proveedor');
    } finally {
      setIsProcessing(false);
    }
  };

  // Excel import handler
  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    setError('');

    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows: any[] = XLSX.utils.sheet_to_json(sheet);

      if (rows.length === 0) {
        setError('El archivo Excel no contiene datos');
        setIsImporting(false);
        return;
      }

      // Map Excel columns to supplier fields
      const items = rows.map((row) => ({
        code: String(row['Proveedor'] || '').trim(),
        name: String(row['Nombre_Proveedor'] || '').trim(),
        cuenta_x_pagar: row['CuentaxPagar'] ? String(row['CuentaxPagar']).trim() : null,
        codigo_cta: row['CODIGOCTA'] ? String(row['CODIGOCTA']).trim() : null,
        cod_retencion: row['Cod_Retencion'] ? String(row['Cod_Retencion']).trim() : null,
        rtefte: row['RTEFTE'] != null ? Number(row['RTEFTE']) : null,
        cod_rete: row['CODRETE'] ? String(row['CODRETE']).trim() : null,
        top_rete: row['TOPRETE'] != null ? Number(row['TOPRETE']) : null,
        po_rete: row['PORETE'] != null ? Number(row['PORETE']) : null,
        cod_cc: row['CODCC'] ? String(row['CODCC']).trim() : null,
      }));

      const result = await api.importSuppliers(items);
      queryClient.invalidateQueries({ queryKey: ['admin-suppliers'] });
      setImportResultModal({ open: true, result: result.data });
    } catch (err: any) {
      setError(err.message || 'Error al importar archivo Excel');
    } finally {
      setIsImporting(false);
      // Reset file input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [queryClient]);

  // Form field renderer
  const renderFormFields = (form: SupplierForm, setForm: (f: SupplierForm) => void) => (
    <div className="space-y-4 py-2">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">NIT (Codigo) *</label>
          <Input
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            placeholder="900448300-3"
          />
        </div>
        <div>
          <label className="text-sm font-medium">Nombre *</label>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Nombre del proveedor"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">Cuenta x Pagar</label>
          <Input
            value={form.cuenta_x_pagar}
            onChange={(e) => setForm({ ...form, cuenta_x_pagar: e.target.value })}
            placeholder="Cuenta x Pagar"
          />
        </div>
        <div>
          <label className="text-sm font-medium">Codigo Cta</label>
          <Input
            value={form.codigo_cta}
            onChange={(e) => setForm({ ...form, codigo_cta: e.target.value })}
            placeholder="Codigo Cta"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">Cod. Retencion</label>
          <Input
            value={form.cod_retencion}
            onChange={(e) => setForm({ ...form, cod_retencion: e.target.value })}
            placeholder="Cod. Retencion"
          />
        </div>
        <div>
          <label className="text-sm font-medium">RTEFTE</label>
          <Input
            type="number"
            value={form.rtefte}
            onChange={(e) => setForm({ ...form, rtefte: e.target.value })}
            placeholder="0"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">Cod. Rete</label>
          <Input
            value={form.cod_rete}
            onChange={(e) => setForm({ ...form, cod_rete: e.target.value })}
            placeholder="Cod. Rete"
          />
        </div>
        <div>
          <label className="text-sm font-medium">Top Rete</label>
          <Input
            type="number"
            value={form.top_rete}
            onChange={(e) => setForm({ ...form, top_rete: e.target.value })}
            placeholder="0"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">PO Rete</label>
          <Input
            type="number"
            value={form.po_rete}
            onChange={(e) => setForm({ ...form, po_rete: e.target.value })}
            placeholder="0"
          />
        </div>
        <div>
          <label className="text-sm font-medium">Cod. CC</label>
          <Input
            value={form.cod_cc}
            onChange={(e) => setForm({ ...form, cod_cc: e.target.value })}
            placeholder="Cod. CC"
          />
        </div>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );

  // Access control
  if (currentUser?.rol !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold">Acceso denegado</h2>
          <p className="text-muted-foreground mt-2">Solo los administradores pueden acceder a esta pagina.</p>
          <Button onClick={() => navigate('/')} className="mt-4">Volver al Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Building className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold">Equivalencias</h1>
                  <p className="text-sm text-muted-foreground">
                    {totalSuppliers} proveedor{totalSuppliers !== 1 ? 'es' : ''} registrado{totalSuppliers !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={isImporting}
              >
                <Upload className={`h-4 w-4 mr-2 ${isImporting ? 'animate-pulse' : ''}`} />
                {isImporting ? 'Importando...' : 'Importar Excel'}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xls,.xlsx"
                className="hidden"
                onChange={handleFileSelect}
              />
              <Button onClick={() => { setCreateModal(true); setError(''); setCreateForm({ ...emptyForm }); }}>
                <Plus className="h-4 w-4 mr-2" />
                Crear Proveedor
              </Button>
            </div>
          </div>

        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search bar */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por NIT o nombre..."
              className="pl-10"
            />
          </div>
        </div>

        {/* Data table */}
        <div className="bg-white rounded-lg shadow">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-2 text-muted-foreground">Cargando proveedores...</p>
            </div>
          ) : suppliers.length === 0 ? (
            <div className="p-8 text-center">
              <Building className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">
                {debouncedSearch ? 'No se encontraron proveedores con esa busqueda' : 'No hay proveedores registrados'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>NIT</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Cuenta x Pagar</TableHead>
                  <TableHead>Codigo Cta</TableHead>
                  <TableHead>Cod. Retencion</TableHead>
                  <TableHead>RTEFTE</TableHead>
                  <TableHead>Cod. Rete</TableHead>
                  <TableHead>Top Rete</TableHead>
                  <TableHead>PO Rete</TableHead>
                  <TableHead>Cod. CC</TableHead>
                  <TableHead>Equivalencias</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {suppliers.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-mono text-sm">{s.code}</TableCell>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{s.cuenta_x_pagar || '-'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{s.codigo_cta || '-'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{s.cod_retencion || '-'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{s.rtefte ?? '-'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{s.cod_rete || '-'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{s.top_rete ?? '-'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{s.po_rete ?? '-'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{s.cod_cc || '-'}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant={(s as any).equivalencies_count > 0 ? 'default' : 'outline'}>
                        {(s as any).equivalencies_count || 0}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={s.is_active ? 'success' : 'destructive'}>
                        {s.is_active ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Equivalencias"
                          onClick={() => navigate(`/admin/suppliers/${s.id}/equivalencias`)}
                        >
                          <Link className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Editar"
                          onClick={() => openEditModal(s)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:text-red-700"
                          title="Eliminar"
                          onClick={() => { setDeleteModal({ open: true, supplier: s }); setError(''); }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-sm text-muted-foreground">
                Pagina {page} de {totalPages} ({totalSuppliers} proveedores)
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Modal Crear Proveedor */}
      <Dialog open={createModal} onOpenChange={(open) => { if (!open) { setCreateModal(false); setError(''); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Crear Proveedor
            </DialogTitle>
            <DialogDescription>Complete los datos para registrar un nuevo proveedor.</DialogDescription>
          </DialogHeader>
          {renderFormFields(createForm, setCreateForm)}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModal(false)} disabled={isProcessing}>Cancelar</Button>
            <Button onClick={handleCreate} disabled={isProcessing}>
              {isProcessing ? 'Creando...' : 'Crear Proveedor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Proveedor */}
      <Dialog open={editModal.open} onOpenChange={(open) => { if (!open) { setEditModal({ open: false, supplier: null }); setError(''); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Editar Proveedor
            </DialogTitle>
            <DialogDescription>Modifique los datos del proveedor.</DialogDescription>
          </DialogHeader>
          {renderFormFields(editForm, setEditForm)}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal({ open: false, supplier: null })} disabled={isProcessing}>Cancelar</Button>
            <Button onClick={handleEdit} disabled={isProcessing}>
              {isProcessing ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Eliminar Proveedor */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => { if (!open) setDeleteModal({ open: false, supplier: null }); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Eliminar Proveedor
            </DialogTitle>
            <DialogDescription>
              Esta seguro que desea eliminar este proveedor? Esta accion no se puede deshacer.
              {deleteModal.supplier && (
                <div className="mt-4 p-3 bg-muted rounded-md">
                  <p className="font-medium">{deleteModal.supplier.name}</p>
                  <p className="text-sm">NIT: {deleteModal.supplier.code}</p>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModal({ open: false, supplier: null })} disabled={isProcessing}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isProcessing}>
              {isProcessing ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Resultado Importacion */}
      <Dialog open={importResultModal.open} onOpenChange={(open) => { if (!open) setImportResultModal({ open: false, result: null }); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Resultado de Importacion
            </DialogTitle>
          </DialogHeader>
          {importResultModal.result && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-700">{importResultModal.result.total_submitted}</p>
                  <p className="text-xs text-blue-600">Total enviados</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-700">{importResultModal.result.imported}</p>
                  <p className="text-xs text-green-600">Importados</p>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-amber-700">{importResultModal.result.valid_items}</p>
                  <p className="text-xs text-amber-600">Validos</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-red-700">{importResultModal.result.errors.length}</p>
                  <p className="text-xs text-red-600">Errores</p>
                </div>
              </div>
              {importResultModal.result.errors.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Errores:</p>
                  <div className="max-h-40 overflow-y-auto bg-red-50 rounded-lg p-3 space-y-1">
                    {importResultModal.result.errors.map((err, i) => (
                      <p key={i} className="text-xs text-red-700">{err}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setImportResultModal({ open: false, result: null })}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
